import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import { AppIconButton, AppListPage } from './design-system';
import {
  Edit,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAppToolbar } from '../contexts/AppToolbarContext';
import { providerTenantsService, type Tenant } from '../services/ProviderTenantsService';
import { providerSupportService } from '../services/ProviderSupportService';
import TenantDetails from './TenantDetails';
import CreateTenantDialog from './dialogs/CreateTenantDialog';
import SensitiveActionDialog, {
  type SensitiveActionTarget,
  type SensitiveActionType,
} from './dialogs/SensitiveActionDialog';
import * as dt from '../../../shared/src/data.types';
import { EnhancedTable, type TableColumn, type TableAction } from './ui/EnhancedTable';

const TenantManagement: React.FC<{ readOnly?: boolean }> = ({ readOnly = false }) => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [sensitiveAction, setSensitiveAction] = useState<SensitiveActionType | null>(null);
  const [sensitiveTarget, setSensitiveTarget] = useState<SensitiveActionTarget | null>(null);
  const [loadingSensitiveTarget, setLoadingSensitiveTarget] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info' as 'success' | 'error' | 'warning' | 'info',
  });
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [showTenantDetails, setShowTenantDetails] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const tenantsData = readOnly
        ? (await providerSupportService.getTenants()).map((tenant): Tenant => ({
            id: tenant.id as dt.Uuid,
            name: tenant.name,
            subdomain: tenant.subdomain,
            createdAt: tenant.createdAt,
            updatedAt: tenant.updatedAt,
          }))
        : await providerTenantsService.getTenants();
      setTenants(tenantsData);
    } catch (error) {
      showSnackbar('Failed to load tenants: ' + (error instanceof Error ? error.message : 'Unknown error'), 'error');
    } finally {
      setLoading(false);
    }
  }, [readOnly]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const openTenantSensitiveAction = async (
    tenant: Tenant,
    action: Extract<SensitiveActionType, 'resend-tenant-invitation' | 'delete-tenant'>,
  ) => {
    try {
      setLoadingSensitiveTarget(true);
      const users = await providerTenantsService.getTenantUsers(tenant.id);
      const admin = users.find((user) => user.role === dt.ALL_USER_ROLES.tenant_admin);
      if (!admin?.email) {
        showSnackbar('Could not find tenant admin email for confirmation.', 'error');
        return;
      }

      setSensitiveTarget({
        id: tenant.id,
        email: admin.email,
        displayName: tenant.name,
      });
      setSensitiveAction(action);
    } catch (error) {
      showSnackbar(
        'Failed to load tenant admin details: ' +
          (error instanceof Error ? error.message : 'Unknown error'),
        'error',
      );
    } finally {
      setLoadingSensitiveTarget(false);
    }
  };

  const closeSensitiveAction = () => {
    setSensitiveAction(null);
    setSensitiveTarget(null);
  };

  const handleSensitiveActionConfirm = async (stepUpToken: string) => {
    if (!sensitiveAction || !sensitiveTarget) {
      return;
    }

    try {
      if (sensitiveAction === 'resend-tenant-invitation') {
        await providerTenantsService.resendTenantInvitation(
          sensitiveTarget.id as dt.Uuid,
          stepUpToken,
        );
        showSnackbar('Invitation email resent to tenant admin successfully!', 'success');
      } else if (sensitiveAction === 'delete-tenant') {
        await providerTenantsService.deleteTenant(sensitiveTarget.id as dt.Uuid, stepUpToken);
        showSnackbar('Tenant deleted successfully!', 'success');
        loadData();
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      showSnackbar(`Failed to complete action: ${errorMessage}`, 'error');
      throw error;
    }
  };

  const handleViewTenant = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setShowTenantDetails(true);
  };

  const handleCloseTenantDetails = () => {
    setShowTenantDetails(false);
    setSelectedTenant(null);
  };

  const handleTenantUpdated = (updatedTenant: Tenant) => {
    setTenants(tenants.map(t => t.id === updatedTenant.id ? updatedTenant : t));
    showSnackbar('Tenant updated successfully', 'success');
  };

  const columns: TableColumn<Tenant>[] = [
    {
      field: 'id',
      headerName: 'ID',
      flex: 1,
      minWidth: 280,
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'subdomain',
      headerName: 'Subdomain',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'customerManagedEncryption',
      headerName: 'Encryption',
      flex: 0.8,
      minWidth: 120,
      renderCell: (row: Tenant) => (
        <Chip
          label={row.customerManagedEncryption ? 'BYOK' : 'Provider-managed'}
          color={row.customerManagedEncryption ? 'secondary' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
  ];

  const actions: TableAction<Tenant>[] = readOnly
    ? [
        {
          label: 'View',
          icon: <Edit />,
          onClick: (row: Tenant) => handleViewTenant(row),
          color: 'primary',
        },
      ]
    : [
    {
      label: 'View/Edit',
      icon: <Edit />,
      onClick: (row: Tenant) => handleViewTenant(row),
      color: 'primary',
    },
    {
      label: 'Resend Invitation',
      icon: <EmailIcon />,
      onClick: (row: Tenant) => void openTenantSensitiveAction(row, 'resend-tenant-invitation'),
      disabled: () => loadingSensitiveTarget,
      color: 'info',
    },
    {
      label: 'Delete',
      icon: <DeleteIcon />,
      onClick: (row: Tenant) => void openTenantSensitiveAction(row, 'delete-tenant'),
      disabled: () => loadingSensitiveTarget,
      color: 'error',
    },
  ];

  const toolbarPageActions = useMemo(
    () => (
      <AppIconButton onClick={() => void loadData()} disabled={loading} aria-label="Refresh">
        <RefreshIcon />
      </AppIconButton>
    ),
    [loadData, loading],
  );

  useAppToolbar({ pageActions: toolbarPageActions });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <AppListPage>
      <EnhancedTable<Tenant>
        data={tenants}
        totalCount={tenants.length}
        columns={columns}
        actions={actions}
        getRowId={(row) => row.id}
        searchable
        searchTerm={searchTerm}
        searchPlaceholder="Search tenants..."
        onSearchChange={setSearchTerm}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onAdd={readOnly ? undefined : () => setCreateDialogOpen(true)}
        addLabel="Add tenant"
        emptyStateTitle="No tenants found"
        emptyStateDescription={readOnly ? 'No tenants are available' : 'Start by adding your first tenant'}
        maxHeight="100%"
      />

      {!readOnly && (
      <CreateTenantDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={(message) => showSnackbar(message, 'success')}
        onError={(message) => showSnackbar(message, 'error')}
        onTenantCreated={loadData}
      />
      )}

      {/* Tenant Details Dialog */}
      <TenantDetails
        open={showTenantDetails}
        tenant={selectedTenant}
        onClose={handleCloseTenantDetails}
        onTenantUpdated={handleTenantUpdated}
        readOnly={readOnly}
      />

      <SensitiveActionDialog
        open={sensitiveAction !== null && sensitiveTarget !== null}
        action={sensitiveAction}
        target={sensitiveTarget}
        onClose={closeSensitiveAction}
        onConfirm={handleSensitiveActionConfirm}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AppListPage>
  );
};

export default TenantManagement;
